import { useCallback } from "react";
import type { RefObject } from "react";

import { css } from "@compiled/react";
import { defineMessages, useIntl } from "react-intl";

import { token } from "@atlaskit/tokens";

import {
    computeDocumentSectionScrollProgress,
    getNearestVerticalScrollContainer,
    useSpiderwebDocumentSectionProgress
} from "@/components/adg4/spiderweb-container/src";
import { wacToken } from "@/libraries/design/foundations";

const dotsBarStyle = css({
    "position": "sticky",
    "insetBlockEnd": 0,
    "zIndex": 2,
    "display": "flex",
    "justifyContent": "center",
    "alignItems": "center",
    "gap": "10px",
    "paddingBlock": token("space.150"),
    "paddingInline": token("space.200"),
    "@media (max-width: 767px)": {
        display: "none"
    }
});

const dotButtonStyle = css({
    "width": "10px",
    "height": "10px",
    "padding": 0,
    "border": "none",
    "borderRadius": "50%",
    "cursor": "pointer",
    "backgroundColor": wacToken("color.neutral.light600"),
    "transition": "background-color 0.2s ease, transform 0.2s ease",
    "&:hover": {
        transform: "scale(1.15)"
    }
});

const dotButtonActiveStyle = css({
    backgroundColor: wacToken("color.primary.blue"),
    transform: "scale(1.2)"
});

const messages = defineMessages({
    dot1: {
        id: "teamworkGraph.scrollDots.section1",
        defaultMessage: "Scroll to introduction"
    },
    dot2: {
        id: "teamworkGraph.scrollDots.section2",
        defaultMessage: "Scroll to first frame"
    },
    dot3: {
        id: "teamworkGraph.scrollDots.section3",
        defaultMessage: "Scroll to second frame"
    },
    dot4: {
        id: "teamworkGraph.scrollDots.section4",
        defaultMessage: "Scroll to third frame"
    },
    dot5: {
        id: "teamworkGraph.scrollDots.section5",
        defaultMessage: "Scroll to fourth frame"
    },
    dot6: {
        id: "teamworkGraph.scrollDots.section6",
        defaultMessage: "Scroll to end"
    }
});

const dotMessageIds = [
    messages.dot1,
    messages.dot2,
    messages.dot3,
    messages.dot4,
    messages.dot5,
    messages.dot6
] as const;

function scrollSectionToTimelineUnits(
    section: HTMLElement,
    targetTimeline: number
): void {
    const trackH = section.offsetHeight;
    const viewH = window.innerHeight;
    const scrollable = Math.max(0, trackH - viewH);
    if (scrollable <= 0) {
        return;
    }
    const currentP = computeDocumentSectionScrollProgress(section);
    const currentScrolled = currentP * scrollable;
    const targetScrolled = Math.min(
        scrollable,
        Math.max(0, (targetTimeline / 100) * scrollable)
    );
    const delta = targetScrolled - currentScrolled;
    const root = getNearestVerticalScrollContainer(section);
    if (root) {
        root.scrollTo({ top: root.scrollTop + delta, behavior: "smooth" });
    } else {
        window.scrollTo({ top: window.scrollY + delta, behavior: "smooth" });
    }
}

function activeDotIndex(timeline: number, edges: readonly number[]): number {
    for (let i = 0; i < edges.length - 1; i++) {
        const lo = edges[i];
        const hi = edges[i + 1];
        if (typeof lo !== "number" || typeof hi !== "number") {
            continue;
        }
        const isLast = i === edges.length - 2;
        if (timeline >= lo && (isLast ? timeline <= hi : timeline < hi)) {
            return i;
        }
    }
    return Math.max(0, edges.length - 2);
}

export type TeamworkGraphDesktopScrollDotsProps = {
    sectionRef: RefObject<HTMLElement | null>;
    edges: readonly number[];
};

export const TeamworkGraphDesktopScrollDots = ({
    sectionRef,
    edges
}: TeamworkGraphDesktopScrollDotsProps) => {
    const intl = useIntl();
    const { progress } = useSpiderwebDocumentSectionProgress(sectionRef, true);
    const timeline = progress * 100;
    const active = activeDotIndex(timeline, edges);

    const onDotClick = useCallback(
        (index: number) => {
            const el = sectionRef.current;
            if (!el) {
                return;
            }
            const lo = edges[index] ?? 0;
            const hi = edges[index + 1] ?? lo;
            const mid = (lo + hi) / 2;
            scrollSectionToTimelineUnits(el, mid);
        },
        [edges, sectionRef]
    );

    const dotCount = edges.length - 1;

    return (
        <div css={dotsBarStyle} role="navigation" aria-label="Story sections">
            {Array.from({ length: dotCount }, (_, i) => {
                const labelDescriptor = dotMessageIds.at(i) ?? messages.dot6;
                return (
                    <button
                        key={i}
                        type="button"
                        css={[
                            dotButtonStyle,
                            i === active && dotButtonActiveStyle
                        ]}
                        aria-current={i === active ? "true" : undefined}
                        aria-label={intl.formatMessage(labelDescriptor)}
                        onClick={() => onDotClick(i)}
                    />
                );
            })}
        </div>
    );
};
